import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';

export interface EmptyStateProps {
  /** Optional MUI icon component (e.g. <InboxIcon />). Renders centered above the title at a large size. */
  icon?: React.ReactNode;
  /** Required title — what is empty. */
  title: string;
  /** Optional description — why it's empty / what to do next. */
  description?: string;
  /**
   * Optional CTA button or link. ReactNode so caller can pass <Button> or <RouterLink>.
   * Takes precedence over actionLabel/onAction when provided.
   */
  action?: React.ReactNode;
  /**
   * Convenience CTA: label for a primary Button with an AddIcon startIcon.
   * Rendered only when BOTH actionLabel and onAction are provided.
   * Use this for the common "create your first record" pattern without having
   * to construct a <Button> in every call-site.
   */
  actionLabel?: string;
  /**
   * Callback fired when the convenience CTA button is clicked.
   * Rendered only when BOTH actionLabel and onAction are provided.
   */
  onAction?: () => void;
  /** Outer Box sx override. */
  sx?: object;
}

export default function EmptyState({
  icon,
  title,
  description,
  action,
  actionLabel,
  onAction,
  sx,
}: EmptyStateProps): React.ReactElement {
  // Derive the rendered CTA: explicit `action` node takes precedence; fall back
  // to the convenience Button when both actionLabel and onAction are supplied.
  const cta: React.ReactNode =
    action ??
    (actionLabel !== undefined && onAction !== undefined ? (
      <Button variant="contained" startIcon={<AddIcon />} onClick={onAction}>
        {actionLabel}
      </Button>
    ) : undefined);

  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        py: 6,
        px: 2,
        gap: 1.5,
        ...sx,
      }}
    >
      {icon && (
        // Tinted rounded tile rather than a bare oversized glyph, matching the
        // icon-tile treatment used by the KPI cards.
        <Box
          aria-hidden
          sx={(theme) => ({
            width: 56,
            height: 56,
            borderRadius: '16px',
            display: 'grid',
            placeItems: 'center',
            color: 'text.secondary',
            backgroundColor: theme.palette.action.hover,
            '& > *': { fontSize: 28 },
          })}
        >
          {icon}
        </Box>
      )}
      <Typography component="h2" sx={{ fontSize: '1rem', fontWeight: 700 }}>
        {title}
      </Typography>
      {description && (
        // Capped so a long explanation stays readable instead of running the
        // full width of a wide table panel.
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '52ch' }}>
          {description}
        </Typography>
      )}
      {cta && <Box sx={{ mt: 2 }}>{cta}</Box>}
    </Box>
  );
}
